import { Card, IconButton, Icon, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Switch } from "@mui/material";
import { useState, useEffect } from "react";
import DataTable from "../../../../Components/Table/DataTable";
import WDBox from "../../../../MaterialComponents/WDBox";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import AppService from "../../../../Configurations/Appservice";
import moment from "moment";
import { CUSTOMER_ORG_TYPE } from "../../../../Configurations/AppConfig";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useNavigate } from "react-router-dom";

const tableInitialData = {
  columns: [
    { Header: "name", accessor: "name" },
    { Header: "organization code", accessor: "code" },
    { Header: "contact person", accessor: "contactPerson" },
    { Header: "contact number", accessor: "contactNumber" },
    // { Header: "address", accessor: "address" },
    { Header: "email", accessor: "email" },
    { Header: "created date", accessor: "createdAt" },
    { Header: "status", accessor: "status", width: "10%" },
    { Header: "edit", accessor: "edit", width: "5%" },
  ],

  rows: [],
};

const CustomerOrganisations = () => {
  const [tableData, setTableData] = useState(tableInitialData);
  const [isLoading, loading] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Dialog state for status change
  const [statusDialog, setStatusDialog] = useState({ open: false, orgId: null, newStatus: false });
  
  // Check if current user is admin
  const isAdmin = () => {
    try {
      const authInfo = JSON.parse(localStorage.getItem("authInfo"));
      return authInfo && authInfo.roles && authInfo.roles.includes("ROOT_ADMIN");
    } catch {
      return false;
    }
  };
  
  const handleStatusChange = async (orgId, newStatus) => {
    try {
      await AppService.put(
        `v1/organisation/active?organisationId=${orgId}&isActive=${newStatus}`
      );
      // Update UI
      setTableData((prev) => {
        const updatedRows = prev.rows.map((row) =>
          row.id === orgId ? { 
            ...row, 
            status: (
              <>
                <span style={{ color: newStatus ? "green" : "gray", fontWeight: "bold" }}>
                  {newStatus ? "Active" : "Inactive"}
                </span>
                <Switch
                  checked={newStatus}
                  color={newStatus ? "success" : "default"}
                  onChange={() => {
                    setStatusDialog({ open: true, orgId: row.id, newStatus: !newStatus });
                  }}
                  style={{ marginLeft: 8 }}
                />
              </>
            ) 
          } : row
        );
        return { ...prev, rows: updatedRows };
      });
    } catch (err) {
      // Error handled globally
    }
  };
  
  useEffect(() => {
    dispatch(updateScreenTitle("Customer Organisations"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const formatCreatedAt = (value) => {
    if (!value) return '-';

    // Some APIs return unix seconds, others return ISO strings.
    if (typeof value === 'number') {
      // Heuristic: treat 13-digit as ms, 10-digit as seconds
      const ms = value > 1e12 ? value : value * 1000;
      return moment(ms).format('DD-MM-YYYY');
    }

    const s = String(value);
    // Parse ISO timestamps reliably in local timezone
    const parsed = moment.utc(s);
    if (!parsed.isValid()) return '-';
    return parsed.local().format('DD-MM-YYYY');
  };

  const _generateTableRows = (response) => {
    const rows = response.map((item) => ({
      id: item.id, // Store ID for reference
      name: item.name,
      code: item.code || '-',
      contactPerson: item.contactPerson,
      address: item.address,
      email: item.email,
      createdAt: formatCreatedAt(item.createdAt),
      contactNumber: item.contactNumber,
      status: (
        <>
          <span style={{ color: item.active ? "green" : "gray", fontWeight: "bold" }}>
            {item.active ? "Active" : "Inactive"}
          </span>
          <Switch
            checked={item.active}
            color={item.active ? "success" : "default"}
            onChange={() => {
              setStatusDialog({ open: true, orgId: item.id, newStatus: !item.active });
            }}
            style={{ marginLeft: 8 }}
          />
        </>
      ),
      edit: (
        <IconButton size="small" onClick={() => _navigateToEditScreen(item.id)}>
          <Icon>edit</Icon>
        </IconButton>
      ),
    }));

    let tableDataDuplicate = { ...tableData };
    tableDataDuplicate.rows = rows;
    setTableData(tableDataDuplicate);
  };

  useEffect(() => {
    const params = {
      type: CUSTOMER_ORG_TYPE,
    };
    AppService.get("v1/organisation", { params })
      .then(_generateTableRows)
      .catch((error) => {})
      .finally(() => {
        loading(false);
      });
  }, []);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-Customer/adm-new-customer-organisation");
  };

  const _navigateToEditScreen = (organisationId) => {
    navigate("/ADM-Customer/adm-edit-customer-organisation", {
      state: {
        organisationId,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* <Card sx={{ mb: 1, p: 2 }}>{_renderSearchBar()}</Card> */}
      {
        <DataTable
          table={tableData}
          showAddButton={true}
          addButtonOnClick={_navigateToCreationScreen}
        />
      }
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ ...statusDialog, open: false })}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to set status to <b>{statusDialog.newStatus ? "Active" : "Inactive"}</b> for this organisation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ ...statusDialog, open: false })}>Cancel</Button>
          <Button
            onClick={() => {
              handleStatusChange(statusDialog.orgId, statusDialog.newStatus);
              setStatusDialog({ ...statusDialog, open: false });
            }}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerOrganisations;
